import React from 'react';

import { Alert, Box, Stack, Typography } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import type {
  ChannelMarkupEvaluationContext,
  TesterScenarioResult,
  TesterValues,
} from './ChannelMarkupTesterPanel.types';

type Props = {
  result: TesterScenarioResult;
  testerValues: TesterValues;
  effectiveTesterContext: ChannelMarkupEvaluationContext;
};

export default function ChannelMarkupTesterResultCard({ result, testerValues, effectiveTesterContext }: Props) {
  const isMatched = result.evaluation.status === 'matched';
  return (
    <Box
      sx={{
        border: 2,
        borderColor: isMatched ? 'success.main' : 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: isMatched ? 'success.50' : 'background.paper',
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">
              {testerValues.context.offer.offerId || 'test-offer'} · {result.currency}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Preview region: {effectiveTesterContext.region ?? 'AU'} · currency {result.currency}
            </Typography>
          </Stack>
          <DebugModal
            type="generic"
            title={`Evaluator result ${result.currency}`}
            data={result}
            hideIcon
            buttonProps={{ size: 'small', variant: 'text' }}
          >
            Debug result
          </DebugModal>
        </Stack>
        <Alert
          severity={isMatched ? 'success' : result.evaluation.status === 'pending' ? 'info' : 'warning'}
          variant="outlined"
        >
          <Stack spacing={0.5}>
            <Typography variant="body2">Evaluation status: {result.evaluation.status}</Typography>
            <Typography variant="body2">Match count: {result.presentation?.matchCount ?? 0}</Typography>
            {result.presentation ? (
              <Typography variant="body2">
                Presented CTA: {result.presentation.channelMarkupCallToActionText || 'n/a'}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No markup matched this scenario.
              </Typography>
            )}
          </Stack>
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Effective inputs: {effectiveTesterContext.offer?.offerId} / {effectiveTesterContext.offer?.loyaltyOfferType} /{' '}
          {effectiveTesterContext.region} / {effectiveTesterContext.currency}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Audience: tier {effectiveTesterContext.subscriberTier ?? 'None'}, role{' '}
          {effectiveTesterContext.roles?.[0] ?? 'None'}
        </Typography>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Condition tests
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {result.conditionEvaluations.length > 0 ? (
              result.conditionEvaluations.map((evaluation) => (
                <Box key={evaluation.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {evaluation.conditionType}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={evaluation.passed ? 'success.main' : 'error.main'}
                    sx={{ fontWeight: 600 }}
                  >
                    {evaluation.passed ? 'Matched' : 'Did not match'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Condition: {evaluation.conditionValue || 'n/a'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Context: {evaluation.contextValue || 'n/a'}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No condition evaluations returned.
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
